import {Autocomplete, AutocompleteRenderInputParams, AutocompleteRenderValue, AutocompleteRenderValueGetItemProps, Button, Chip, createFilterOptions, CreateFilterOptionsConfig, FilterOptionsState, Grid, TextField} from '@mui/material';
import {useField, useFormikContext} from 'formik';
import {FormFieldProps, OptionValue} from '../../model/form';
import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {AddRounded, EditRounded} from "@mui/icons-material";
import {useAppTranslation} from "services/i18n";
import {LoadingSpinner} from "components/loading/LoadingSpinner";
import CircularProgress from "@mui/material/CircularProgress";

interface Props extends FormFieldProps {
    options?: OptionValue[],
    placeholder?: string;
    isMulti?: boolean;
    clearable?: boolean;
    showTooltip?: boolean;
    matchFromStart?: boolean;
    freeSolo?: boolean;
    getTagColor?: (option: OptionValue) => string;
    editProps?: {
        modal: (
            value: any,
            onCancel: () => void,
            onSave: (res: any) => void
        ) => JSX.Element
    },
    onSearch?: (search: string) => void,
    currentOptions?: OptionValue[],
    renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: OptionValue) => JSX.Element,
    shrink?: boolean,
    isLoading?: boolean,
    isInitializing?: boolean
}

/**
 * What keeps Chrome's native suggestions off a picker input, the same way the AngularJS
 * md-autocomplete pickers do it: a search field with autocomplete off. On a plain text field
 * Chrome goes by the label ("Jméno a příjmení") and covers our own list with its saved names
 * whatever the autocomplete token says. Spread over the TextField of every Autocomplete,
 * together with pickerSlotProps.
 */
export const pickerInputProps = {
    type: 'search',
    sx: {
        // a search field draws its own clear button, next to our clear/open icons
        'input::-webkit-search-cancel-button, input::-webkit-search-decoration': {
            WebkitAppearance: 'none',
            display: 'none'
        }
    }
};

export const pickerSlotProps = (params: AutocompleteRenderInputParams) => ({
    ...params.slotProps,
    htmlInput: {...params.slotProps.htmlInput, autoComplete: 'off'}
});

const isOptionEqualToValue = (option: OptionValue, value: any) => {
    return option.value + '' === (value?.value ?? value) + '';
}

const getOption = (value: any, options: OptionValue[]): OptionValue | undefined => {
    if (value?.label) {
        // multi
        return value as OptionValue;
    }
    const o = options
        .find((c) => {
            const v = '' + c.value;
            return v === value + '';
        });
    return o || undefined; /* invalid */
}

export const SelectFormField = (props: Props) => {

    const t = useAppTranslation();

    const {
        name, label, options, onChange, isMulti, clearable, placeholder, disabled,
        showTooltip, matchFromStart, freeSolo, getTagColor, editProps, renderOption,
        onSearch, shrink, isLoading, isInitializing
    } = props;

    const [field, meta, helpers] = useField(name);
    const {submitCount} = useFormikContext();

    const [searchStatus, setSearchStatus] = useState<'pending' | 'found' | 'empty'>();

    const [editItem, setEditItem] = useState<any | undefined>(undefined);
    const searchTimeout = useRef<any>();
    const emptyArray = useMemo(() => [], []);

    const showError = !!meta.error && (meta.touched || submitCount > 0);
    const metaError = meta.error;
    const formSetValue = helpers.setValue;

    const renderOptionDefault = useCallback((props: React.HTMLAttributes<HTMLLIElement>, option: OptionValue) => (
        <li {...props} key={'item-' + option.value} title={option.tooltip}>
            {option.icon ? <small>{option.icon} </small> : null}{option.htmlLabel || option.label}
            {showTooltip && option.tooltip ? <>&nbsp;<small>({option.tooltip})</small></> : null}
        </li>
    ), [showTooltip]);

    const getOptionLabel = useCallback((value: any) => {
        return getOption(value, options || [])?.label || (isMulti ? value : '' /* invalid */);

    }, [options, isMulti]);

    const renderInput = useCallback((params: AutocompleteRenderInputParams) => {
        return <TextField
            {...params}
            margin={'dense'}
            fullWidth
            id={name}
            name={name}
            label={label}
            hiddenLabel={!label}
            title={typeof label == 'string' ? label : placeholder}
            error={showError}
            helperText={showError && metaError}
            variant={'standard'}
            size={'small'}
            {...pickerInputProps}
            placeholder={placeholder}
            disabled={disabled}
            slotProps={{
                ...pickerSlotProps(params),
                inputLabel: {
                    ...params.slotProps.inputLabel,
                    shrink
                },
                input: {
                    ...params.slotProps.input,
                    endAdornment: isInitializing ? <CircularProgress color="inherit" size={14} /> : params.slotProps.input.endAdornment
                }
            }}
        />
    }, [showError, metaError, isInitializing, name, label, placeholder, shrink, disabled]);

    const renderValue = useCallback((value: AutocompleteRenderValue<OptionValue, boolean, boolean>, getItemProps: AutocompleteRenderValueGetItemProps<boolean>) => {
        // only ever set on a multi select; its value holds raw option values or OptionValue objects,
        // and an object missing from options is used as is (getOption), so a miss is a raw value
        return (value as (OptionValue | OptionValue['value'])[]).map((option: OptionValue | OptionValue['value'], index: number) => {
            const o = options?.find((o) => isOptionEqualToValue(o, option)) || getOption(option, []);
            const title = o?.label || String(option ?? '');
            return <Chip variant="filled" sx={{
                '&.MuiChip-root.Mui-disabled': {
                    opacity: 1,
                    '.MuiChip-deleteIcon': {
                        display: 'none'
                    }
                },
                '&.MuiChip-root > .MuiChip-label': {
                    display: 'flex',
                    alignItems: 'center'
                },
                '&.MuiChip-root > .MuiChip-label > img ': {
                    marginLeft: '-8px'
                }
            }}
                title={o?.tooltip || (typeof title === 'string' ? title : undefined)}
                style={o && getTagColor ? {backgroundColor: getTagColor(o)} : undefined}
                label={o?.icon ? <>{o?.icon} {title}</> : title}{...getItemProps({index})} />
        })
    }, [options, getTagColor]);

    const handleOnChange = useCallback((_: any, newValue: OptionValue | string | (OptionValue | string)[] | null) => {
        let v: (OptionValue | string | number | boolean)[] | string | undefined;
        if (isMulti) {
            if (newValue instanceof Array) {
                v = newValue.map(o => typeof o === 'string' || o.value === undefined ? o : o.value);
                v = v.filter((item, i) => (v as any[]).indexOf(item) === i); // remove duplicates
            } else {
                v = [];
            }
        } else {
            const vv = freeSolo
                ? (newValue?.hasOwnProperty('value') ? (newValue as any).value : newValue)
                : (newValue as OptionValue)?.value;
            if (vv || vv === 0 || vv === false) {
                v = vv;
            } else {
                v = undefined;
            }
        }

        formSetValue(v, true);
        if (onChange) {
            onChange(v);
        }
    }, [isMulti, freeSolo, onChange, formSetValue]);

    const filterOptions: ((options: OptionValue[], state: FilterOptionsState<OptionValue>) => OptionValue[]) | undefined = useMemo(() => {
        let c: CreateFilterOptionsConfig<OptionValue> = {};
        if (matchFromStart) {
            c['matchFrom'] = "start";
        }
        if (showTooltip) {
            c['stringify'] = (o: OptionValue) => {
                return (o.label + ' ' + o.tooltip).toLowerCase();
            }
        }
        return Object.keys(c).length > 0 ? createFilterOptions(c) : undefined;
    }, [matchFromStart, showTooltip]);

    const onInputChange = useCallback((_: any, value: string) => {
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
            searchTimeout.current = undefined;
        }
        if (value === '' && !onSearch) { // just selected & returning back, keep current
            setSearchStatus(undefined);
            return;
        }
        setSearchStatus('pending');
        searchTimeout.current = setTimeout(() => {
            if (onSearch) {
                onSearch(value);
            }
        }, 250);
    }, [onSearch]);

    useEffect(() => {
        setSearchStatus(current => {
            if (isLoading) {
                return 'pending';
            }
            if (!!options?.length) {
                return 'found';
            }
            return current ? 'empty' : current;
        })

    }, [options, isLoading]);

    const handleOnBlur = useCallback(() => {
        if (isMulti || !field.value || freeSolo) {
            return;
        }
        if (!options?.find(o => isOptionEqualToValue(o, field.value))) {
            formSetValue(undefined, true);
            if (onChange) {
                onChange(undefined);
            }
        }

    }, [options, field.value, isMulti, freeSolo, formSetValue, onChange]);

    useEffect(() => {
        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        }
    }, []);

    const currentValue = (field.value || field.value === 0 || field.value === false) ? field.value : (isMulti ? emptyArray : null);
    const el = <Autocomplete
        fullWidth
        disableClearable={!clearable}
        id={name}
        value={currentValue}
        filterOptions={onSearch ? (x) => x : filterOptions}
        // inputValue={inputValue}
        freeSolo={freeSolo || searchStatus === undefined}
        clearOnBlur={freeSolo}
        autoSelect={freeSolo}
        options={options || emptyArray}
        multiple={isMulti}
        autoHighlight={!freeSolo}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption || renderOptionDefault}
        isOptionEqualToValue={(o, v) => isOptionEqualToValue(o, v)}
        renderInput={renderInput}
        // renderValue also renders a single value, which renderTags never did
        renderValue={isMulti ? renderValue : undefined}
        onChange={handleOnChange}
        onBlur={freeSolo ? undefined : handleOnBlur}
        onInputChange={onSearch ? onInputChange : undefined}
        slotProps={{
            paper: {
                sx: {
                    minWidth: 'clamp(200px, 100%, 100%)',
                    width: 'fit-content'
                }
            }
        }}
        // clearText={t('Vymazat')}
        // closeText={t('Zavřít')}
        noOptionsText={isLoading || searchStatus === 'pending' ? <LoadingSpinner size={30}/> : t('common.emptyList')}
        // openText={t('Otevřít')}
        sx={{
            // the design system chip is 22px tall, which fits inside the input's own
            // line box -- a field with chips then keeps its underline level with the
            // plain fields next to it
            '& .MuiAutocomplete-tag': {
                height: '22px',
                fontSize: '12px',
                margin: '2px 8px 2px 0',
                '& .MuiChip-label': {
                    paddingLeft: '10px',
                    paddingRight: '6px'
                },
                '& .MuiChip-deleteIcon': {
                    fontSize: '14px',
                    margin: '0 6px 0 0',
                    opacity: .6
                },
                // the people pickers put an avatar in the chip; keep it inside the pill
                '& .MuiChip-label .MuiAvatar-root': {
                    width: '18px',
                    height: '18px',
                    fontSize: '9px',
                    margin: '0 4px 0 -4px'
                }
            },
            // the theme pins the input box to a fixed height but Autocomplete
            // overrides only its padding, which left the select 3px taller than a
            // plain field -- restoring that padding puts both underlines on one line
            '& .MuiAutocomplete-inputRoot.MuiInput-root': {
                paddingBottom: 0,
                '& .MuiAutocomplete-input': {
                    padding: '2px 4px 1px 0'
                }
            },
            // '& .MuiInput-root .MuiAutocomplete-tag + .MuiInput-input': {
            //     marginLeft: '8px'
            // }
        }}
        disabled={disabled}
    />;

    if (editProps?.modal) {
        return <><Grid container>
            <Grid sx={{flexGrow: 1}}>
                {el}
            </Grid>
            <Grid><Button variant={'contained'} color={'inherit'} className={'quick-add-button'} onClick={() => {
                setEditItem(currentValue);
            }}> {currentValue ? <EditRounded/> : <AddRounded/>}</Button>
            </Grid>
        </Grid>
            {editItem !== undefined && editProps.modal(editItem,
                () => {
                    setEditItem(undefined);
                },
                (newValue) => {
                    formSetValue(newValue, true);
                    setEditItem(undefined);
                    if (onChange) {
                        onChange(newValue);
                    }
                })}
        </>;
    }
    return el;
}
